"use strict";
/**
 * Arm state for the MARK button (ai/USER_PRICE_SAMPLE_PLAN.md). The DB enforces
 * one sample per order via a partial unique index; this is the UI half.
 *
 * Two-step because the halves are known at different times: a click's CONTEXT
 * (which click, which market was on screen, its fan-out size) at click time,
 * but whether anything FILLED only when a response returns — and a zero-fill
 * IOC must not arm, since there is no position to score.
 *
 * ARMS ACCUMULATE. Several clicks into one position is the normal way to trade
 * here, and the operator then forms ONE judgement about where the price
 * settled; a single press marks every click they made on this market, each
 * still getting exactly one sample (they produce disjoint legs, so the unique
 * index is untouched). Accumulating also removes an ordering trap: a single
 * arm slot follows RESPONSE order, so a slow first order returning after a fast
 * second one would leave the button armed on the older click with nothing on
 * screen to say so.
 *
 * Disarm rules all live in getArmedSamples() so no caller can skip one:
 *   • sampled, or terminally rejected (per click)
 *   • the operator switched market — the two-stage order button's invariant
 *     (ai/GOTCHAS.md §3); here the on-screen book would be the wrong market's.
 *     Leaving forfeits EVERY armed click, since they all belong to that book.
 *   • ARM_TTL_MS elapsed, each click on its own clock
 *
 * Mirrored to localStorage so a refresh doesn't cost the operator their sample.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ARM_TTL_MS = void 0;
exports.toSampleVenue = toSampleVenue;
exports.registerClick = registerClick;
exports.reportClickFill = reportClickFill;
exports.getArmedSamples = getArmedSamples;
exports.disarmClicks = disarmClicks;
exports.disarm = disarm;
exports.subscribeArmChange = subscribeArmChange;
exports.resetPriceSampleArmForTest = resetPriceSampleArmForTest;
const state_1 = require("../state");
/** How long an arm stays live. Long enough to watch a line settle. */
exports.ARM_TTL_MS = 10 * 60 * 1000;
const STORAGE_KEY = "kalshi_price_sample_arm";
/** Cap on remembered click contexts; only bounds a pathological loop. */
const MAX_PENDING = 8;
/**
 * Cap on simultaneously armed clicks — one press fires one request each, so
 * this bounds the fan-out. Oldest is dropped: with far more armed clicks than
 * this, the recent ones are the ones the operator is judging.
 */
const MAX_ARMED = 20;
const pending = new Map();
/** Every click armed on the current market, oldest first. */
let armed = [];
let loadedFromStorage = false;
const listeners = new Set();
/** The client's data source as the wire venue; null for anything unsupported. */
function toSampleVenue(dataSource) {
    if (dataSource === "kalshi")
        return "kalshi";
    if (dataSource === "pm-us")
        return "polymarket_us";
    if (dataSource === "novig")
        return "novig";
    // Legacy Polymarket orders aren't logged as sampleable legs.
    return null;
}
function notify() {
    const snapshot = armed.slice();
    for (const listener of listeners) {
        try {
            listener(snapshot);
        }
        catch {
            // A broken listener must not break arming.
        }
    }
}
function persist() {
    try {
        if (armed.length > 0)
            localStorage.setItem(STORAGE_KEY, JSON.stringify(armed));
        else
            localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // Private mode / quota — the arm still works for this page view.
    }
}
/** A stored entry, or null when it isn't one. */
function parseArmed(raw) {
    const entry = raw;
    if (typeof entry?.clickId !== "string" ||
        typeof entry.marketId !== "string" ||
        (entry.venue !== "kalshi" && entry.venue !== "polymarket_us" && entry.venue !== "novig") ||
        typeof entry.expiresAt !== "number") {
        return null;
    }
    return {
        clickId: entry.clickId,
        marketId: entry.marketId,
        venue: entry.venue,
        expectedRequests: typeof entry.expectedRequests === "number"
            ? entry.expectedRequests
            : 1,
        armedAt: typeof entry.armedAt === "number" ? entry.armedAt : 0,
        expiresAt: entry.expiresAt,
    };
}
/** Read the persisted arms once per page load. Never throws on junk. */
function loadOnce() {
    if (loadedFromStorage)
        return;
    loadedFromStorage = true;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return;
        const parsed = JSON.parse(raw);
        // Pre-accumulation builds stored a single object; read either shape.
        const entries = Array.isArray(parsed) ? parsed : [parsed];
        armed = entries
            .map(parseArmed)
            .filter((entry) => entry !== null);
        if (armed.length === 0)
            localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // Unparseable — treat as no arm.
    }
}
/** Remember a click's context so a later fill can arm it. */
function registerClick(context) {
    if (pending.size >= MAX_PENDING) {
        const oldest = pending.keys().next();
        if (!oldest.done)
            pending.delete(oldest.value);
    }
    pending.set(context.clickId, context);
}
/**
 * Report one order response. Arms the click when a BUY actually filled —
 * matching what the interval tracker and the sample endpoint consider
 * scoreable. Called from both venue paths in execution.ts, so a parallel or
 * additional order that fills arms the click just as a primary one does.
 *
 * Runs after the exchange ack, never before it, so it adds nothing to the
 * click→exchange path. Must not throw into the order flow.
 */
function reportClickFill(clickId, action, filledQty) {
    try {
        if (!clickId || action !== "buy" || !(filledQty > 0))
            return;
        const context = pending.get(clickId);
        if (!context)
            return;
        pending.delete(clickId);
        const now = Date.now();
        // Arms from an earlier market are dropped wholesale, never mixed: the
        // press records ONE book, which belongs to one market.
        if (armed.length > 0 && armed[0].marketId !== context.marketId) {
            armed = [];
        }
        armed.push({ ...context, armedAt: now, expiresAt: now + exports.ARM_TTL_MS });
        if (armed.length > MAX_ARMED)
            armed = armed.slice(-MAX_ARMED);
        persist();
        notify();
    }
    catch {
        // Arming is a convenience; never let it disturb an order response.
    }
}
/**
 * Every live arm, oldest first. Applies the expiry and market-change rules on
 * read, so a stale arm can never be acted on even if nothing told us it went
 * stale.
 */
function getArmedSamples() {
    loadOnce();
    if (armed.length === 0)
        return [];
    const currentMarketId = (0, state_1.getState)().selectedMarket?.id ?? null;
    const now = Date.now();
    const live = armed.filter((entry) => now < entry.expiresAt && entry.marketId === currentMarketId);
    if (live.length !== armed.length) {
        armed = live;
        persist();
        // No notify(): this runs inside the button's own render pass, and
        // re-entering it would recurse.
    }
    return armed.slice();
}
/** Drop specific clicks (sampled, or terminally rejected). Idempotent. */
function disarmClicks(clickIds) {
    if (clickIds.length === 0 || armed.length === 0)
        return;
    const drop = new Set(clickIds);
    const kept = armed.filter((entry) => !drop.has(entry.clickId));
    if (kept.length === armed.length)
        return;
    armed = kept;
    persist();
    notify();
}
/** Drop every arm. Idempotent. */
function disarm() {
    if (armed.length === 0)
        return;
    armed = [];
    persist();
    notify();
}
/** Subscribe to arm changes. Returns an unsubscribe function. */
function subscribeArmChange(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
/** TEST-ONLY: clear all arm state, in memory and in storage. */
function resetPriceSampleArmForTest() {
    pending.clear();
    armed = [];
    loadedFromStorage = false;
    listeners.clear();
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // nothing to clear
    }
}
